import { fetchBatchAttendees, fetchEvents } from "../../apis/eventsApi";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { v4 as uuidv4 } from "uuid";
import { fetchClosures } from "../../apis/clousuresApi";
import { message } from "antd";
import utc from 'dayjs/plugin/utc';
import timezone from "dayjs/plugin/timezone";
import { fetchBranchConfig } from "../../apis/branchApi";
import BranchConfig from "../../enums/branch_config";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(timezone);


let clousures = [];

export const loadEvents = async (
  branchId,
  fromDate,
  toDate,
  onlyCourtesies = false,
  filterPast = true
) => {
  const tz = await fetchBranchConfig(BranchConfig.TIMEZONE);

  fromDate = fromDate.tz(tz).startOf("day").utc();
  toDate = toDate.tz(tz).endOf("day").utc();

  try{
    clousures = await fetchClosures(branchId, fromDate, toDate);
  }catch (error) {
    console.error("loadEvents - Error fetching closures:", error);
    message.error("Error de cargar los eventos. Intenta más tarde.");
    return;
  }

  const result = await fetchEvents(branchId, fromDate, toDate, onlyCourtesies);
  // Ensure uniqueEvents have start_date and end_date as dayjs objects
  const generatedEvents = result.uniqueEvents
    .filter((event) => event.skips_closures || !isEventWithinClosure(event))
    .map((event) => ({
      ...event,
      event_id: event.id,
      is_unique: true,
      start_date: dayjs.utc(event.start_date),
      end_date: dayjs.utc(event.end_date),
      unique_id: uuidv4(), // add unique_id to match with attendees load
    }));

  let current = dayjs.utc(fromDate);

  while (current.isSameOrBefore(toDate, "day")) {
    const day = current;
    result.recurringEvents.forEach((event) => {
      const eventStartDate = dayjs.utc(
        `${day.format("YYYY-MM-DD")} ${event.start_time}`
      );

      if (day.isBefore(dayjs.utc(event.first_recurrence), "day")
         || (event.deleted_at && day.isAfter(dayjs.utc(event.deleted_at)))
         || eventStartDate.isBefore(fromDate)
         || eventStartDate.isAfter(toDate)
         || (event.end_recurrency && eventStartDate.isAfter(dayjs.utc(event.end_recurrency)))) {
         return;
      }
      switch (event.frequency) {
        case "DAY":
          processDailyEvent(
            event,
            day,
            result,
            generatedEvents,
            fromDate,
            toDate,
            onlyCourtesies
          );
          break;
        case "WEEK":
          processWeeklyEvent(
            event,
            day,
            result,
            generatedEvents,
            fromDate,
            toDate,
            onlyCourtesies
          );
          break;
        default:
          console.log("NextSessions - Error: unexpected frequency");
      }
    });
    current = current.add(1, "day");
  }

  generatedEvents.sort((a, b) => dayjs.utc(a.start_date).diff(dayjs.utc(b.start_date)));

  // Filter past events if filterPast is true
  let filteredEvents = generatedEvents;
  if (filterPast) {
    const now = dayjs.utc();
    filteredEvents = generatedEvents.filter((ev) =>
      dayjs.utc(ev.start_date).isAfter(now)
    );
  }

  const eventsWithAttendees = await fetchBatchAttendees(filteredEvents);

  return eventsWithAttendees;
};

const processDailyEvent = (
  event,
  current,
  result,
  generatedEvents,
  fromDate,
  toDate,
  onlyCourtesies
) => {
  const isMatchingRecurrency =
    current.diff(dayjs.utc(event.first_recurrence), "day") % event.interval === 0;

  if (!isMatchingRecurrency) return;

  processRecurrentEvent(
    event,
    current,
    result,
    generatedEvents,
    fromDate,
    toDate,
    onlyCourtesies
  );
};

const processWeeklyEvent = (
  event,
  current,
  result,
  generatedEvents,
  fromDate,
  toDate,
  onlyCourtesies
) => {
  const isMatchingRecurrency =
    current.diff(dayjs.utc(event.first_recurrence), "week") % event.interval === 0 &&
    event.days_of_week.includes(current.day().toString());

  if (!isMatchingRecurrency) return;

  processRecurrentEvent(
    event,
    current,
    result,
    generatedEvents,
    fromDate,
    toDate,
    onlyCourtesies
  );
};

const processRecurrentEvent = (
  event,
  current,
  result,
  generatedEvents,
  fromDate,
  toDate,
  onlyCourtesies
) => {
  const eventStartDate = dayjs.utc(
    `${current.format("YYYY-MM-DD")} ${event.start_time}`
  );
  let eventEndDate;
  if (event.end_time > event.start_time) {
    eventEndDate = dayjs.utc(
      `${current.format("YYYY-MM-DD")} ${event.end_time}`
    );
  } else {
    eventEndDate = dayjs.utc(
        `${current.add(1, "day").format("YYYY-MM-DD")} ${event.end_time}`
      );
  }

  const { applyException, processedEvent, isDeleted } = applyExceptionIfExists(
    event,
    eventStartDate,
    eventEndDate,
    result.recurringExeptions,
    fromDate,
    toDate,
    onlyCourtesies
  );

  if (isDeleted) {
    return generatedEvents;
  }

  if (applyException) {
    processedEvent.is_edited = true;
    processedEvent.unique_id = uuidv4(); // add unique_id to match with attendees load
    generatedEvents.push(processedEvent);
    return generatedEvents;
  }

  if (onlyCourtesies && !event.enabled_for_courtesy) {
    return generatedEvents;
  }

  if (clousures.some((closure) =>
        eventStartDate.isBetween(dayjs.utc(closure.start_date), dayjs.utc(closure.end_date), null, "[]")
      )
    ) {
      return generatedEvents;
    }

  generatedEvents.push({
    ...event,
    start_date: eventStartDate,
    end_date: eventEndDate,
    unique_id: uuidv4(), // add unique_id to match with attendees load
  });

  return generatedEvents;
};

const applyExceptionIfExists = (
  event,
  eventStartDate,
  eventEndDate,
  recurringExceptions,
  fromDate,
  toDate,
  onlyCourtesies
) => {
  const exception = recurringExceptions.find(
    (ex) =>
      ex.event_id === event.event_id &&
      dayjs.utc(ex.original_start_date)
        .isSame(eventStartDate)
  );

  if (!exception) {
    return { applyException: false };
  }

  if (exception && exception.is_deleted === 1) {
    return { isDeleted: true };
  }
  // !exception no sería ya false? sino abría salido en el if ante-anterior
  if (!exception && onlyCourtesies && !exception.enabled_for_courtesy) {
    return { applyException: false };
  }

  const baseEvent = {
    ...event,
    name: exception.new_name ?? event.name,
    place: exception.new_place ?? event.place,
    quotas: exception.new_quotas ?? event.quotas,
  };

  if (exception.new_start_date) {
    const newStart = dayjs.utc(exception.new_start_date);
    const newEnd = dayjs.utc(exception.new_end_date);

    // Check if the event is within the closure dates
    if (
      !exception.skips_closures &&
      clousures.some((closure) =>
        newStart.isBetween(dayjs.utc(closure.start_date), dayjs.utc(closure.end_date), null, "[]")
      )
    ) {
      return { applyException: false };
    }

    if (
      newStart.isSameOrAfter(fromDate, "day") &&
      newStart.isSameOrBefore(toDate, "day")
    ) {
      return {
        applyException: true,
        processedEvent: {
          ...baseEvent,
          start_date: newStart,
          end_date: newEnd,
          original_start_date: exception.original_start_date, // needed for backend delete/update
        },
      };
    } else {
      return { applyException: false };
    }
  }

  return {
    applyException: true,
    processedEvent: {
      ...baseEvent,
      start_date: eventStartDate,
      end_date: eventEndDate,
      original_start_date: exception.original_start_date, // needed for backend delete/update
    },
  };
};

const isEventWithinClosure = (event) => {
  let eventStartDate = dayjs.utc(event.start_date);
  return clousures.some((closure) => {
  const closure_start = dayjs.utc(closure.start_date)
  const closure_end = dayjs.utc(closure.end_date)
    return (
      eventStartDate.isBetween(closure_start, closure_end, null, "[]")
    );
  });
};